import axios from 'axios';
import api from './api';

/**
 * Fetches users with specified roles
 * @param {Array} roleNames - Array of role names to filter by
 * @param {Function} errorCallback - Callback function to execute on error
 * @returns {Promise<Array>} - Array of users
 */
export const fetchUsersByRoles = async (roleNames = [], errorCallback = null) => {
  try {
    // Coba endpoint standar untuk mendapatkan users berdasarkan role
    const endpoints = [
      `/users/?role__in=${roleNames.join(',')}`,
      `/users/?role_in=${roleNames.join(',')}`,
      `/users/by-roles/?roles=${roleNames.join(',')}`,
      '/users/'
    ];
    
    let users = [];
    let lastError = null;
    
    // Coba setiap endpoint secara berurutan sampai berhasil
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying to fetch users from ${endpoint}`);
        const response = await api.get(endpoint);
        
        if (response.data) {
          const userData = response.data?.results || (Array.isArray(response.data) ? response.data : []);
          
          // Jika ini endpoint standar users, filter berdasarkan role
          if (endpoint === '/users/') {
            console.log("Filtering users by roles manually");
            
            users = userData.filter(user => {
              // Cek berbagai format role
              const userRole = typeof user.role === 'string' ? 
                user.role.toLowerCase() : 
                user.role?.name?.toLowerCase() || '';
              
              // Cek apakah userRole ada di roleNames
              return roleNames.some(role => 
                userRole.includes(role.toLowerCase()) || 
                // Special case untuk owner/admin
                (role === 'owner' && (userRole === 'admin' || user.username === 'mbotee'))
              );
            });
          } else {
            users = userData;
          }
          
          if (users.length > 0) {
            console.log(`Success with endpoint ${endpoint}, found ${users.length} users`);
            break;
          }
        }
      } catch (err) {
        console.warn(`Failed to fetch from ${endpoint}:`, err);
        lastError = err;
      }
    }
    
    // Jika masih tidak berhasil dan ini mode pengembangan, gunakan data contoh
    if (users.length === 0 && process.env.NODE_ENV === 'development') {
      console.warn("Using mock data for development");
      users = [
        { id: 1, username: "mbotee", first_name: "Muhammad", last_name: "Botee", role: "owner" },
        { id: 2, username: "agung", first_name: "Agung", last_name: "", role: "marketing" },
        { id: 3, username: "adi_junction", first_name: "Adi", last_name: "Junianto", role: "marketing" },
        { id: 4, username: "asyhuri", first_name: "Asyhuri", last_name: "", role: "marketing" },
        { id: 5, username: "anditusanto", first_name: "Andi", last_name: "Tusanto", role: "general manager" }
      ];
    }
    
    return users;
  } catch (error) {
    console.error("Error in fetchUsersByRoles:", error);
    if (errorCallback) {
      errorCallback(error);
    }
    return [];
  }
};

/**
 * Format user data for dropdown selection
 * @param {Object} user - User object
 * @returns {Object} - Formatted user for dropdown
 */
export const formatUserForDropdown = (user) => {
  if (!user) return null;
  
  // Nama tampilan
  let displayName = '';
  
  if (user.first_name || user.last_name) {
    displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  } else {
    displayName = user.username || 'Unknown';
  }
  
  // Role display
  let roleDisplay = '';
  if (user.role) {
    if (typeof user.role === 'string') {
      roleDisplay = user.role;
    } else if (user.role.name) {
      roleDisplay = user.role.name;
    }
  }
  
  return {
    id: user.id.toString(),
    name: displayName,
    role: roleDisplay,
    username: user.username
  };
};

/**
 * Function untuk mendapatkan user ID dari storage secara konsisten
 */
export const getCurrentUserId = () => {
  // Coba berbagai kemungkinan key untuk user ID
  return (
    localStorage.getItem('userId') || 
    localStorage.getItem('user_id') || 
    localStorage.getItem('id')
  );
};

/**
 * Function untuk mendapatkan username dari storage
 */
export const getCurrentUsername = () => {
  return localStorage.getItem('username');
};

/**
 * Function untuk mendapatkan informasi user saat ini
 */
export const getCurrentUser = () => {
  return {
    id: getCurrentUserId(),
    username: getCurrentUsername(),
    role: localStorage.getItem('role')
  };
};